import type { Step as RcStep } from '@sb/remote-control/types';

import type { Diagnostic, RoutineView, ValidationRule } from '../../types';
import {
  catchActionRequiredToDiagnostic,
  createStepRule,
  diag,
} from '../../util/ruleHelpers';
import { validateConditional } from '../../util/validators';

/**
 * Validate the Antigravity step's condition, if present.
 *
 * System A checks:
 * 1. Standard conditional validation (same as If/ElseIf)
 * 2. IO-only constraint: only `ioState` variable kinds are allowed
 */
function validateAntigravityCondition(
  routine: RoutineView,
  stepId: string,
): Diagnostic[] {
  const stepCfg = routine.stepConfigurations[stepId];
  if (!stepCfg) return [];

  const args = stepCfg.args as RcStep.Arguments & {
    condition?: string | null;
  };

  if (args.condition == null) return [];

  let conditions: Array<
    Array<{ variable?: { kind: string }; value?: unknown }>
  >;

  try {
    conditions = JSON.parse(args.condition || '[]');
  } catch {
    return [
      diag({
        code: 'ui.invalidConfiguration',
        message: 'Condition is not valid JSON.',
        stepId,
        sourceRule: 'steps.Antigravity',
        fieldId: 'condition',
      }),
    ];
  }

  // 1. Standard conditional validation
  try {
    validateConditional(conditions, routine);
  } catch (err) {
    return catchActionRequiredToDiagnostic(err, stepId, 'steps.Antigravity');
  }

  // 2. IO-only constraint: only ioState variable kinds are allowed
  for (const orGroup of conditions) {
    for (const condition of orGroup) {
      if (condition.variable && condition.variable.kind !== 'ioState') {
        return [
          diag({
            code: 'ui.invalidConfiguration',
            message:
              'Only IO conditions are allowed for the alternative end condition.',
            stepId,
            sourceRule: 'steps.Antigravity',
            fieldId: 'condition',
          }),
        ];
      }
    }
  }

  return [];
}

export const AntigravityValidation: ValidationRule = createStepRule({
  id: 'steps.Antigravity',
  stepKinds: ['Antigravity'],
  run: ({ routine, stepId }) => {
    const diagnostics: Diagnostic[] = [];

    const conditionDiags = validateAntigravityCondition(routine, stepId);
    diagnostics.push(...conditionDiags);

    return diagnostics;
  },
});
